import {
  WarehouseSyncAzureBlobConfig,
  WarehouseSyncGCSConfig,
  WarehouseSyncS3Config,
  WarehouseSyncYandexConfig,
} from 'cio/models/warehouse-sync';
import { KeysOfUnion } from 'cio/utils/generic-types';
import { IntegrationIDs, IntegrationKeys, IntegrationNames } from 'cio/utils/integrations/static-integrations';

export enum WarehouseDataOptionID {
  deliveries = 'deliveries',
  metrics = 'metrics',
  subjects = 'subjects',
  outputs = 'outputs',
  people = 'people',
  attributes = 'attributes',
  contents = 'contents',
}

export type WarehouseDataOption = {
  id: WarehouseDataOptionID;
  name: string;
  description: string;
};

const WAREHOUSE_DATA_OPTIONS: Readonly<WarehouseDataOption[]> = Object.freeze([
  {
    id: WarehouseDataOptionID.deliveries,
    name: 'Deliveries',
    description: 'One record for each email, push, SMS, slack, or webhook message created.',
  },
  {
    id: WarehouseDataOptionID.contents,
    name: 'Delivery Contents',
    description:
      'One record for each email, push, SMS, slack, or webhook message created. Each record contains the actual content sent out.',
  },
  {
    id: WarehouseDataOptionID.metrics,
    name: 'Metrics',
    description: 'One record for each event that occurs for a message (eg. delivered, bounced, opened, clicked).',
  },
  {
    id: WarehouseDataOptionID.subjects,
    name: 'Subjects',
    description: 'One record for each workflow journey started.',
  },
  {
    id: WarehouseDataOptionID.outputs,
    name: 'Outputs',
    description: 'One record for each discrete step in a workflow journey.',
  },
  {
    id: WarehouseDataOptionID.people,
    name: 'People',
    description: "A list of people IDs and if they've been deleted or suppressed.",
  },
  {
    id: WarehouseDataOptionID.attributes,
    name: 'Attributes',
    description: 'One record for each attribute change on a profile.',
  },
]);

export const WAREHOUSE_SCHEMA_V1_OPTIONS = WAREHOUSE_DATA_OPTIONS.filter((opt) =>
  [WarehouseDataOptionID.deliveries, WarehouseDataOptionID.metrics, WarehouseDataOptionID.people].includes(opt.id),
);

export const WAREHOUSE_SCHEMA_V2_V3_SUPPORTED_OPTIONS = WAREHOUSE_DATA_OPTIONS.filter((opt) =>
  [
    WarehouseDataOptionID.deliveries,
    WarehouseDataOptionID.metrics,
    WarehouseDataOptionID.people,
    WarehouseDataOptionID.attributes,
  ].includes(opt.id),
);

export const WAREHOUSE_SCHEMA_V4_OPTIONS = WAREHOUSE_DATA_OPTIONS.filter((opt) =>
  [
    WarehouseDataOptionID.deliveries,
    WarehouseDataOptionID.contents,
    WarehouseDataOptionID.metrics,
    WarehouseDataOptionID.people,
    WarehouseDataOptionID.subjects,
    WarehouseDataOptionID.outputs,
    WarehouseDataOptionID.attributes,
  ].includes(opt.id),
);

export type WarehouseDestination = `${WarehouseDestinationID}`;

export type WarehouseStorageConfig =
  | WarehouseSyncAzureBlobConfig
  | WarehouseSyncGCSConfig
  | WarehouseSyncS3Config
  | WarehouseSyncYandexConfig;

export type WarehouseStorageConfigKeys = KeysOfUnion<WarehouseStorageConfig>;

export enum WarehouseDestinationID {
  s3 = 's3',
  gcs = 'gcs',
  yandex = 'yandex',
  azure = 'azure_blob',
}

export function getWarehouseDestinationImageSrc(wh: WarehouseDestination): string {
  return {
    s3: new URL('cio/assets/images/integrations/s3.svg', import.meta.url).toString(),
    [IntegrationIDs.gcs]: new URL(
      'cio/assets/images/integrations/google-cloud-storage.svg',
      import.meta.url,
    ).toString(),
    [IntegrationIDs.yandex]: new URL('cio/assets/images/integrations/yandex.svg', import.meta.url).toString(),
    [IntegrationIDs.azureBlob]: new URL(
      'cio/assets/images/integrations/azure-blob-storage.svg',
      import.meta.url,
    ).toString(),
  }[wh];
}

export function getWarehouseDestinationName(wh: WarehouseDestination): string {
  return {
    s3: IntegrationNames.amazonS3,
    [IntegrationIDs.gcs]: IntegrationNames.gcs,
    [IntegrationIDs.yandex]: 'Yandex Cloud',
    [IntegrationIDs.azureBlob]: IntegrationNames.azureBlob,
  }[wh];
}

export function getWarehouseDocumentationUrl(wh: WarehouseDestination): string {
  return {
    s3: 'https://customer.io/docs/amazon-s3-data-out',
    [IntegrationIDs.gcs]: 'https://customer.io/docs/gcs-data-out',
    [IntegrationIDs.yandex]: 'https://customer.io/docs/yandex-data-out',
    [IntegrationIDs.azureBlob]: 'https://customer.io/docs/ms-azure-data-out',
  }[wh];
}

export const IntegrationDestinationConfig: Record<
  IntegrationKeys,
  'azureConfig' | 'gcsConfig' | 's3Config' | 'yandexConfig'
> = {
  [IntegrationIDs.azureBlob]: 'azureConfig',
  [IntegrationIDs.gcs]: 'gcsConfig',
  s3: 's3Config',
  [IntegrationIDs.yandex]: 'yandexConfig',
};
